import React, { memo, useEffect, useState } from "react";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Collapse from "@mui/material/Collapse";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import Paper from "@mui/material/Paper";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import { grey } from "@mui/material/colors";
import { useSelector, useDispatch } from "react-redux";
import configData from "../../../config.js";
import {
  initialState,
  setDepartmentFilters,
  resetDepartmentFilters,
  showNotifier,
} from "../../../store/globalSlice";
import useRequest from "../../../hooks/useRequest";
import useStableCallback from "../../../hooks/useStableCallback";
import CustomDropdown from "../../../components/CustomDropdown";
import CustomAutoComplete from "../../../components/CustomAutoComplete";

const PaperSx = {
  display: "flex",
  justifyContent: "center",
  flexDirection: "column",
  pl: 1,
  mb: 1,
  "& .MuiTypography-root": {
    fontSize: "0.75rem",
    fontWeight: 500,
  },
  "& .note": {
    color: () => grey[500],
  },
};

const CenterAlignSx = { display: "flex", alignItems: "center" };

const CustomDropdownSx = {
  fontSize: "10px",
  width: "11rem",
  ml: 1,
};

const HelperTextSx = { mt: 0, height: 0, fontSize: "0.6rem", fontWeight: 500 };

const ErrorSx = { border: "1px solid red", borderRadius: "3px" };

const INIT_ERRORS = {
  deptName: "",
  vendorName: "",
  pog: "",
  sku: "",
};

export const groupBy = [
  "Department",
  "Category",
  "Planogram",
  "Planogram #",
  "SKU",
  "Vendor",
];

export const groupMapping = {
  Category: "Class",
  Planogram: "Sub Class",
  "Planogram #": "Planogram",
};

function DepartmentSearch({
  handleSearch = () => {},
  handleReset = () => {},
  freeze = true,
}) {
  const { requestMaker } = useRequest();
  const dispatch = useDispatch();
  const {
    viewBy,
    department,
    category,
    planogram,
    planogramValue,
    vendorName,
    skuValue,
    _deptLetter,
    _pogName,
    _sku,
  } = useSelector((store) => store.global.departmentFilters);
  const { id: storeId } = useSelector((store) => store.global.store);
  const [deptName, setDeptName] = useState([]);
  const [clsName, setClassName] = useState([]);
  const [subName, setSubName] = useState([]);
  const [open, setOpen] = useState([groupBy[1], groupBy[2]].includes(viewBy));
  const [filters, setFilters] = useState({
    viewBy,
    department,
    category,
    planogram,
    planogramValue,
    vendorName,
    skuValue,
    _deptLetter,
    _pogName,
    _sku,
  });
  const [errors, setErrors] = useState(INIT_ERRORS);
  const [pogInputValue, setPogInputValue] = useState("");
  const [pogOptions, setPogOptions] = useState(
    !!planogramValue ? [planogramValue] : []
  );
  const [vendorInputValue, setVendorInputValue] = useState("");
  const [vendorOptions, setVendorOptions] = useState([]);
  const [skuInputValue, setSkuInputValue] = useState("");
  const [skuOptions, setSkuOptions] = useState([]);
  const [optionsLoading, setOptionsLoading] = useState(false);

  useEffect(() => {
    requestMaker({
      method: "post",
      url: configData.GetAllDepartment,
      data: {
        StoreId: storeId,
      },
    }).then((res) => {
      setDeptName(res?.data);
    });
  }, [requestMaker, dispatch, storeId]);

  useEffect(() => {
    if (deptName.length > 0 && !!filters?.department) {
      const deptValue = filters?.department.split("-");
      requestMaker({
        method: "post",
        url: configData.GetClassByDepartment,
        data: { departmentName: deptValue[1] },
      }).then((res) => {
        setClassName(res?.data);
      });
    } else {
      setClassName([]);
    }
  }, [requestMaker, deptName.length, filters?.department]);

  useEffect(() => {
    if (clsName.length > 0 && !!filters?.category) {
      requestMaker({
        method: "post",
        url: configData.GetSubClassByClass,
        data: { className: filters?.category },
      }).then((res) => {
        setSubName(res?.data);
      });
    } else {
      setSubName([]);
    }
  }, [requestMaker, clsName.length, filters?.category]);

  const handleExpansion = useStableCallback(() => {
    setOpen(!open);
  });

  const getAutoCompleteOptions = useStableCallback(
    (pogValue = null, vendorName = null, skuNumber = null) => {
      setOptionsLoading(true);
      requestMaker({
        method: "post",
        url: configData.GetDeptFilters,
        data: {
          PlanogramValue: pogValue,
          VendorName: vendorName,
          SkuNumber: skuNumber,
          StoreId: storeId,
          IsStoreSupplies: false,
        },
      })
        .then((res) => {
          const list = typeof res?.data === "object" ? res?.data : [];
          if (!!pogValue) {
            setPogOptions([...list]);
          }
          if (!!vendorName) {
            setVendorOptions([...list]);
          }
          if (!!skuNumber) {
            setSkuOptions([...list]);
          }
        })
        .catch((err) =>
          dispatch(
            showNotifier({
              message: "Something went wrong. Please try again.",
              severity: "error",
            })
          )
        )
        .finally(() => setOptionsLoading(false));
    }
  );

  const handleViewBy = useStableCallback((e) => {
    const newValue = e?.target?.value;
    setErrors(INIT_ERRORS);
    setFilters({
      ...filters,
      viewBy: newValue,
      department: null,
      category: null,
      planogram: null,
      planogramValue: null,
      vendorName: null,
      skuValue: null,
      _deptLetter: null,
      _pogName: null,
      _sku: null,
    });
    if ([groupBy[1], groupBy[2]].includes(newValue)) {
      setOpen(true);
    } else {
      setOpen(false);
    }
  });

  const handleDepartmentChange = useStableCallback((e) => {
    const newValue = e?.target?.value;
    const valueArray = newValue.split("-");
    if (!!newValue) {
      setErrors({ ...errors, deptName: "" });
    }
    setSubName([]);
    setClassName([]);
    setFilters({
      ...filters,
      department: newValue,
      category: null,
      planogram: null,
      _deptLetter: !!newValue ? valueArray[0] : null,
    });
  });

  const handleCategoryChange = useStableCallback((e) => {
    setSubName([]);
    setFilters({
      ...filters,
      category: e?.target?.value,
      planogram: null,
    });
  });

  const handlePlanogramChange = useStableCallback((e) => {
    setFilters({
      ...filters,
      planogram: e?.target?.value,
    });
  });

  const handlePogValueChange = useStableCallback((e, newValue) => {
    const valueArray = newValue.split("-");
    setFilters({
      ...filters,
      planogramValue: newValue,
      _deptLetter: !!newValue ? valueArray[0] : null,
      _pogName: !!newValue ? valueArray[1] : null,
    });
  });

  const handlePogInputChange = useStableCallback((e, newInputValue) => {
    const inputArray = newInputValue.split("-");
    setPogInputValue(newInputValue);
    if (!newInputValue) {
      setErrors({ ...errors, pog: "This field is required." });
      setPogOptions(!!planogramValue ? [planogramValue] : []);
    } else {
      const value = inputArray.length > 1 ? inputArray[1] : newInputValue;
      setErrors({ ...errors, pog: "" });
      if (value.length >= 2) {
        getAutoCompleteOptions(value);
      }
    }
  });

  const handleVendorNameChange = useStableCallback((e, newValue) => {
    setFilters({
      ...filters,
      vendorName: newValue,
    });
  });

  const handleVendorInputChange = useStableCallback((e, newInputValue) => {
    setVendorInputValue(newInputValue);
    if (!newInputValue) {
      setErrors({ ...errors, vendorName: "This field is required." });
      setVendorOptions(!!vendorName ? [vendorName] : []);
    } else {
      setErrors({ ...errors, vendorName: "" });
      if (newInputValue.length >= 3) {
        getAutoCompleteOptions(null, newInputValue, null);
      }
    }
  });

  const handleSkuValueChange = useStableCallback((e, newValue) => {
    const valueArray = newValue.split("-");
    setFilters({
      ...filters,
      skuValue: newValue,
      departmentName: !!newValue ? valueArray[0] : null,
      planogramValue: !!newValue ? valueArray[1] : null,
      _deptLetter: !!newValue ? valueArray[0] : null,
      _pogName: !!newValue ? valueArray[1] : null,
      _sku: !!newValue ? valueArray[2] : null,
    });
  });

  const handleSkuInputChange = useStableCallback((e, newInputValue) => {
    const inputArray = newInputValue.split("-");
    setSkuInputValue(newInputValue);
    if (!newInputValue) {
      setErrors({ ...errors, sku: "This field is required." });
      setSkuOptions(!!skuValue ? [skuValue] : []);
    } else {
      setErrors({ ...errors, sku: "" });
      if (inputArray.length < 3 && isNaN(Number(newInputValue))) {
        setErrors({ ...errors, sku: "Please enter a valid SKU number." });
      } else {
        const value = inputArray.length > 1 ? inputArray[2] : newInputValue;
        setErrors({ ...errors, sku: "" });
        if (String(value).length >= 5) {
          getAutoCompleteOptions(null, null, Number(value));
        }
      }
    }
  });

  const onSubmit = useStableCallback(() => {
    // Validations for filters
    if (
      [groupBy[1], groupBy[2]].includes(filters?.viewBy) &&
      !filters?.department
    ) {
      return setErrors({ ...errors, deptName: "This field is required." });
    }
    if (groupBy[5] === filters?.viewBy && !filters?.vendorName) {
      return setErrors({ ...errors, vendorName: "This field is required." });
    }
    if (groupBy[4] === filters?.viewBy && !filters?.skuValue) {
      return setErrors({ ...errors, sku: "This field is required." });
    }
    if (groupBy[3] === filters?.viewBy && !filters?.planogramValue) {
      return setErrors({ ...errors, pog: "This field is required." });
    }
    setErrors(INIT_ERRORS);
    dispatch(setDepartmentFilters({ field: "viewBy", value: filters?.viewBy }));
    dispatch(
      setDepartmentFilters({ field: "department", value: filters?.department })
    );
    dispatch(
      setDepartmentFilters({ field: "category", value: filters?.category })
    );
    dispatch(
      setDepartmentFilters({ field: "planogram", value: filters?.planogram })
    );
    dispatch(
      setDepartmentFilters({
        field: "planogramValue",
        value: filters?.planogramValue || filters?._pogName,
      })
    );
    dispatch(
      setDepartmentFilters({ field: "vendorName", value: filters?.vendorName })
    );
    dispatch(
      setDepartmentFilters({ field: "skuValue", value: filters?.skuValue })
    );
    dispatch(
      setDepartmentFilters({
        field: "_deptLetter",
        value: filters?._deptLetter,
      })
    );
    dispatch(
      setDepartmentFilters({ field: "_pogName", value: filters?._pogName })
    );
    dispatch(setDepartmentFilters({ field: "_sku", value: filters?._sku }));

    handleSearch(filters, true);
  });

  const onReset = useStableCallback(() => {
    dispatch(resetDepartmentFilters());
    setFilters(initialState?.departmentFilters);
    setOpen(false);
    setErrors(INIT_ERRORS);
    handleReset(initialState?.departmentFilters);
  });

  return (
    <form noValidate autoComplete="off">
      <Paper sx={PaperSx}>
        <Grid
          container
          justifyContent="space-between"
          pb={!!errors?.sku || !!errors?.pog || !!errors?.vendorName ? 1.5 : 0}
        >
          <Grid container item xs alignItems="center" columnGap={1}>
            <Grid item xs="auto" sx={CenterAlignSx}>
              <Typography>View Department By:</Typography>
              <CustomDropdown
                inputsx={CustomDropdownSx}
                value={filters?.viewBy}
                onChange={handleViewBy}
                disabled={freeze}
              >
                {groupBy.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </CustomDropdown>
            </Grid>
            {filters?.viewBy === groupBy[0] && (
              <Grid item xs="auto" sx={CenterAlignSx}>
                <Typography>Department:</Typography>
                <CustomDropdown
                  inputsx={CustomDropdownSx}
                  value={filters?.department ?? ""}
                  onChange={handleDepartmentChange}
                  disabled={freeze}
                >
                  <option aria-label="None" value="">
                    {!deptName.length ? "Loading..." : "None"}
                  </option>
                  {deptName.map((option) => (
                    <option
                      key={option.DepartmentName}
                      value={option.DepartmentName}
                    >
                      {option.DepartmentName}
                    </option>
                  ))}
                </CustomDropdown>
              </Grid>
            )}
            {filters?.viewBy === groupBy[3] && (
              <Grid item xs="auto" sx={CenterAlignSx}>
                <Typography mr={1}>Planogram #/Name *:</Typography>
                <FormControl>
                  <CustomAutoComplete
                    value={filters?.planogramValue}
                    disabled={freeze}
                    inputValue={pogInputValue}
                    loading={optionsLoading}
                    options={pogOptions}
                    onChange={handlePogValueChange}
                    onInputChange={handlePogInputChange}
                    inputSx={{ width: "11rem" }}
                    inputProps={{ maxLength: 100 }}
                    customSx={{ ...(!!errors?.pog && ErrorSx) }}
                  />
                  <FormHelperText
                    error={!!errors?.pog}
                    sx={{ ...HelperTextSx, ml: 0 }}
                  >
                    {errors?.pog}
                  </FormHelperText>
                </FormControl>
                <Typography className="note" ml={0.5}>
                  (Enter min. 2 characters)
                </Typography>
              </Grid>
            )}
            {filters?.viewBy === groupBy[4] && (
              <Grid item xs="auto" sx={CenterAlignSx}>
                <Typography mr={1}>SKU *:</Typography>
                <FormControl>
                  <CustomAutoComplete
                    value={filters?.skuValue}
                    disabled={freeze}
                    inputValue={skuInputValue}
                    options={skuOptions}
                    loading={optionsLoading}
                    onChange={handleSkuValueChange}
                    onInputChange={handleSkuInputChange}
                    inputSx={{ width: "11rem" }}
                    inputProps={{ maxLength: 6 }}
                    customSx={{ ...(!!errors?.sku && ErrorSx) }}
                  />
                  <FormHelperText
                    error={!!errors?.sku}
                    sx={{ ...HelperTextSx, ml: 0 }}
                  >
                    {errors?.sku}
                  </FormHelperText>
                </FormControl>
                <Typography className="note" ml={0.5}>
                  (Enter min. 5 characters)
                </Typography>
              </Grid>
            )}
            {filters?.viewBy === groupBy[5] && (
              <Grid item xs="auto" sx={CenterAlignSx}>
                <Typography mr={1}>Vendor *:</Typography>
                <FormControl>
                  <CustomAutoComplete
                    value={filters?.vendorName}
                    disabled={freeze}
                    inputValue={vendorInputValue}
                    loading={optionsLoading}
                    options={vendorOptions}
                    onChange={handleVendorNameChange}
                    onInputChange={handleVendorInputChange}
                    inputSx={{ width: "11rem" }}
                    customSx={{ ...(!!errors?.vendorName && ErrorSx) }}
                  />
                  <FormHelperText
                    error={!!errors?.vendorName}
                    sx={{ ...HelperTextSx, ml: 0 }}
                  >
                    {errors?.vendorName}
                  </FormHelperText>
                </FormControl>
                <Typography className="note" ml={0.5}>
                  (Enter min. 3 characters)
                </Typography>
              </Grid>
            )}
            <Grid item xs="auto">
              <IconButton
                aria-label="search"
                color="primary"
                size="small"
                onClick={onSubmit}
                sx={{ px: 0 }}
                disabled={freeze}
              >
                <SearchIcon fontSize="small" />
              </IconButton>
            </Grid>
            <Grid item xs="auto">
              <IconButton
                aria-label="reset"
                color="primary"
                size="small"
                onClick={onReset}
                sx={{ px: 0 }}
                disabled={freeze}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Grid>
          </Grid>
          {[groupBy[1], groupBy[2]].includes(filters?.viewBy) && (
            <Grid item xs="auto">
              <IconButton
                size="small"
                aria-label="expand"
                component="label"
                onClick={handleExpansion}
              >
                {!open ? (
                  <KeyboardArrowRightIcon fontSize="inherit" />
                ) : (
                  <KeyboardArrowDownIcon fontSize="inherit" />
                )}
              </IconButton>
            </Grid>
          )}
        </Grid>
        <Collapse in={open} timeout="auto" unmountOnExit>
          <Grid
            container
            alignItems="center"
            columnGap={1}
            rowGap={1}
            pt={0.5}
            pb={!!errors?.deptName ? 2.5 : 1}
          >
            {[groupBy[1], groupBy[2]].includes(filters?.viewBy) && (
              <Grid item xs={12} sm={6} md={3.5} lg={2.8} sx={CenterAlignSx}>
                <Typography>Department *:</Typography>
                <FormControl>
                  <CustomDropdown
                    disabled={freeze}
                    inputsx={{
                      ...CustomDropdownSx,
                      ...(!!errors?.deptName && ErrorSx),
                    }}
                    value={filters?.department ?? ""}
                    onChange={handleDepartmentChange}
                    required={[groupBy[1], groupBy[2]].includes(
                      filters?.viewBy
                    )}
                  >
                    <option aria-label="None" value="">
                      {!deptName.length ? "Loading..." : "None"}
                    </option>
                    {deptName.map((option) => (
                      <option
                        key={option.DepartmentName}
                        value={option.DepartmentName}
                      >
                        {option.DepartmentName}
                      </option>
                    ))}
                  </CustomDropdown>
                  <FormHelperText error={!!errors?.deptName} sx={HelperTextSx}>
                    {errors?.deptName}
                  </FormHelperText>
                </FormControl>
              </Grid>
            )}
            {[groupBy[1], groupBy[2]].includes(filters?.viewBy) && (
              <Grid item xs={12} sm={6} md={3.5} lg={2.8} sx={CenterAlignSx}>
                <Typography>Category:</Typography>
                <CustomDropdown
                  disabled={freeze}
                  inputsx={CustomDropdownSx}
                  value={filters?.category ?? ""}
                  onChange={handleCategoryChange}
                >
                  <option aria-label="None" value="">
                    {!clsName.length && !!filters.department
                      ? "Loading..."
                      : "None"}
                  </option>
                  {clsName.map((option) => (
                    <option key={option.ClassName} value={option.ClassName}>
                      {option.ClassName}
                    </option>
                  ))}
                </CustomDropdown>
              </Grid>
            )}
            {filters?.viewBy === groupBy[2] && (
              <Grid item xs={12} sm={6} md={3.5} lg={2.8} sx={CenterAlignSx}>
                <Typography>Planogram:</Typography>
                <CustomDropdown
                  disabled={freeze}
                  inputsx={CustomDropdownSx}
                  value={filters?.planogram ?? ""}
                  onChange={handlePlanogramChange}
                >
                  <option aria-label="None" value="">
                    {!subName.length && !!filters.category
                      ? "Loading..."
                      : "None"}
                  </option>
                  {subName.map((option) => (
                    <option
                      key={option.SubClassName}
                      value={option.SubClassName}
                    >
                      {option.SubClassName}
                    </option>
                  ))}
                </CustomDropdown>
              </Grid>
            )}
          </Grid>
        </Collapse>
      </Paper>
    </form>
  );
}

export default memo(DepartmentSearch);
